import { useEffect, useState, useMemo, useCallback } from 'react';
import { PostHogProvider } from 'posthog-js/react';
import type { PartialFlagAtlas } from '@duke-hq/flags';
import posthog from 'posthog-js';
import { FeatureFlagContext, type FeatureFlagEvaluationData } from './featureFlagContext';

// Re-export types for backwards compatibility
export type { FeatureFlagEvaluationData };

/**
 * Type for the flags query hook result.
 * This matches the shape returned by TRPC's useQuery for the flags.getFlags endpoint.
 */
export type FlagsQueryResult = {
  data?: {
    flags: PartialFlagAtlas;
    timestamp: number;
  };
  refetch?: () => void;
};

export type FeatureFlagProviderProps = {
  data: FeatureFlagEvaluationData;
  flags: PartialFlagAtlas;
  // PostHog Public API Key starts with: phc_*
  api_key?: string;
  children: React.ReactNode;
  /**
   * Result from a TRPC flags.getFlags query.
   * The provider will update flags when this data changes.
   *
   * Example usage:
   * ```tsx
   * const flagsQuery = trpc.flags.getFlags.useQuery(undefined, {
   *   refetchInterval: 5 * 60 * 1000, // 5 minutes
   *   refetchOnWindowFocus: true,
   * });
   *
   * <FeatureFlagProvider
   *   data={providerData}
   *   flags={initialFlags}
   *   flagsQueryResult={flagsQuery}
   * >
   * ```
   */
  flagsQueryResult?: FlagsQueryResult;
};

export function FeatureFlagProvider({
  data,
  flags,
  api_key,
  children,
  flagsQueryResult,
}: FeatureFlagProviderProps): React.ReactElement {
  const [isIdentified, setIsIdentified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const currentFlags = useMemo(
    () => flagsQueryResult?.data?.flags ?? flags,
    [flagsQueryResult?.data?.flags, flags],
  );

  const refetchFlags = useCallback(() => {
    flagsQueryResult?.refetch?.();
  }, [flagsQueryResult]);

  // Initialize PostHog for analytics only (not feature flag evaluation)
  useEffect(() => {
    const ph_key = api_key ?? process.env.PUBLIC_POSTHOG_KEY ?? process.env.POSTHOG_KEY;
    if (typeof ph_key === 'string') {
      if (!posthog.__loaded && !hasLoaded && !isLoading) {
        setIsLoading(true);

        posthog.init(ph_key, {
          // Nobody in the team is currently using these and they consistently exceed our
          // quota limits and send annoying emails - happy to re-enable if anyone wants to use
          // them though
          disable_session_recording: true,

          // We don't use posthog surveys in our app
          disable_surveys: true,

          // enable posthog debug mode in local development if PLATFORM_POSTHOG_DEBUG is set
          // The debug flag is passed from the server via the data prop
          debug: data.environment === 'local' && (data.debug ?? false),
          bootstrap: {
            // Identify the user before the posthog object is ready
            distinctID: data?.user?.drn ?? undefined,
            isIdentifiedID: data?.user?.drn !== undefined && data?.user?.drn !== null,
          },

          // Feature flag evaluation is performed server side now and loaded into client side
          // context on page load. We do not use posthog for client side feature flags at all.
          advanced_disable_feature_flags_on_first_load: true,
          advanced_disable_feature_flags: true, // Completely disable client-side flags through posthog

          loaded: (_posthog) => {
            setIsLoading(false);
            setHasLoaded(true);
            if (data.user && !isIdentified) {
              posthog.identify(data.user.drn);
              posthog.group('environment', data.environment);
              setIsIdentified(true);
            }
          },
          on_request_error: (_error) => {
            setIsLoading(false);
          },
        });
      }
    }
  }, [flags]);

  const providerData = useMemo(
    () => ({
      ...data,
      isIdentified,
      posthog,
      initialFlags: currentFlags,
      refetchFlags,
    }),
    [data, isIdentified, currentFlags, refetchFlags],
  );

  return (
    <PostHogProvider client={posthog}>
      <FeatureFlagContext.Provider value={providerData}>{children}</FeatureFlagContext.Provider>
    </PostHogProvider>
  );
}
